import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { getSaved, setSavedLocal } from "@/lib/watchLater";
import { toast } from "@/components/ui/sonner";

const AuthContext = createContext(null);
const PENDING_KEY = "rv_pending_save";
const OWNER_KEY = "rv_saved_owner"; // user_id whose list is mirrored in localStorage
const http = axios.create({ baseURL: API, withCredentials: true });

export const setPendingSave = (id) => (id ? localStorage.setItem(PENDING_KEY, id) : localStorage.removeItem(PENDING_KEY));

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const startGoogleSignIn = () => {
  const redirectUrl = window.location.origin + window.location.pathname + window.location.search;
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prompt, setPrompt] = useState(null); // { reason } when the sign-in modal is open

  // First sign-in on this device: merge the guest's local titles into the account. Afterwards the server is the
  // source of truth (so a title removed on another device stays removed). Then apply any pending guest save.
  const establish = useCallback(async (u) => {
    setUser(u);
    try {
      const firstOnDevice = localStorage.getItem(OWNER_KEY) !== u.user_id;
      let { data } = firstOnDevice
        ? await http.post("/me/saved/merge", { ids: getSaved() })
        : await http.get("/me/saved");
      localStorage.setItem(OWNER_KEY, u.user_id);
      const pending = localStorage.getItem(PENDING_KEY);
      if (pending) {
        setPendingSave(null);
        ({ data } = await http.put(`/me/saved/${encodeURIComponent(pending)}`));
        toast.success("Saved to your Vault");
      }
      setSavedLocal(data.ids);
    } catch {
      /* keep local copy if sync fails */
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check — AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get("auth_error")) {
      toast.error("That sign-in link has expired or was already used. Request a new one.");
      params.delete("auth_error");
      const qs = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
    http.get("/auth/me")
      .then(({ data }) => establish(data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, [establish]);

  const exchangeSession = useCallback(async (sessionId) => {
    try {
      const { data } = await http.post("/auth/session", { session_id: sessionId });
      await establish(data);
      toast.success(`Signed in as ${data.name || data.email}`);
      return data;
    } catch (err) {
      // Fall back to any existing session so an already signed-in user doesn't appear logged out.
      await http.get("/auth/me").then(({ data }) => establish(data)).catch(() => {});
      throw err;
    }
  }, [establish]);

  const requestMagicLink = useCallback(async (email) => {
    const next = window.location.pathname + window.location.search;
    await http.post("/auth/magic-link", { email, next });
  }, []);

  const logout = useCallback(async () => {
    await http.post("/auth/logout").catch(() => {});
    setUser(null);
    localStorage.removeItem(OWNER_KEY);
    setSavedLocal([]); // don't leave this account's list on a shared device
    toast("Signed out");
  }, []);

  const value = {
    user,
    loading,
    prompt,
    openSignIn: (reason = "save", pendingId = null) => {
      setPendingSave(pendingId);
      setPrompt({ reason });
    },
    closeSignIn: () => setPrompt(null),
    exchangeSession,
    requestMagicLink,
    logout,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

// Fire-and-forget server sync for signed-in users (local list is already updated optimistically).
export const syncSaved = (action, id) => {
  const req =
    action === "add" ? http.put(`/me/saved/${encodeURIComponent(id)}`)
    : action === "remove" ? http.delete(`/me/saved/${encodeURIComponent(id)}`)
    : http.delete("/me/saved");
  return req.then(({ data }) => setSavedLocal(data.ids)).catch(() => toast.error("Couldn't sync your Vault — will retry on next visit"));
};
